import React, {useState, useEffect, useRef, useCallback} from "react";
import {
    Accordion, AccordionDetails, AccordionGroup, AccordionSummary,
    Badge,
    Box,
    Button,
    Card,
    Chip,
    Divider,
    FormLabel,
    Input,
    List,
    ListItem,
    ListItemDecorator,
    Sheet,
    Step,
    StepIndicator,
    Stepper,
    Typography
} from "@mui/joy";
import logo from "../images/yeti-clean-logo.png";
import crm from "../images/crm.png";
import marketing from "../images/marketing.png";
import profits from "../images/profits.png";
import scaling from "../images/scaling.png";
import tracking from "../images/tracking.png";
import franchisee from "../images/franchisee.png";
import territory from "../images/territory.png";
import ads from "../images/ads.png";
import franchise_pack from "../images/franchise-pack.png";
import video_thumbnail from "../images/video_thumbnail.png";
import calendar from "../images/calendar.png";
import {
    AccountBalance,
    AdsClick,
    ArrowBack,
    BusinessCenter,
    CalendarMonthOutlined,
    CallMadeRounded,
    Check,
    CurrencyPound,
    DiamondOutlined,
    Done,
    DoneAllRounded,
    DoneOutline,
    DoneOutlineTwoTone,
    DoneTwoTone,
    FileCopy,
    FileDownload,
    HdrPlus,
    LocalShipping,
    LockSharp,
    MapRounded, PinDrop,
    PlusOne,
    TrendingUp
} from "@mui/icons-material";
import ReactBeforeSliderComponent from 'react-before-after-slider-component';
import 'react-before-after-slider-component/dist/build.css';
import {AddLead} from "./Api";
import {useNavigate} from "react-router-dom";

export default function Landing() {
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [franchiseType, setFranchiseType] = useState("");
    const [territoryLocation, setTerritoryLocation] = useState("");
    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [highlightCard, setHighlightCard] = useState("");
    const navigate = useNavigate();
    const autocompleteInputRef = useRef(null);
    const autocompleteRef = useRef(null);

    const initAutocomplete = useCallback(() => {
        if (autocompleteInputRef.current && window.google && window.google.maps && !autocompleteRef.current) {
            autocompleteRef.current = new window.google.maps.places.Autocomplete(
                autocompleteInputRef.current,
                {
                    types: ['(regions)'],
                    componentRestrictions: { country: 'gb' },
                }
            );
            autocompleteRef.current.addListener('place_changed', () => {
                const place = autocompleteRef.current.getPlace();
                const value = (place && place.formatted_address) || (place && place.name) || autocompleteInputRef.current.value;
                setTerritoryLocation(value);
            });
        }
    }, []);

    useEffect(() => {
        if (step === 1) {
            autocompleteRef.current = null;
            const timer = setTimeout(initAutocomplete, 100);
            return () => clearTimeout(timer);
        }
    }, [step, initAutocomplete]);

    const faqData = [
        { question: "What is the initial investment for a YetiClean franchise?", answer: "The initial investment is \u00a312,950, which includes \u00a34,000 worth of professional roof and exterior cleaning equipment. (Page 4 of the prospectus)" },
        { question: "What kind of revenue can I expect?", answer: "Our top franchisees are generating \u00a38,000+ per month in revenue, with some completing 10+ roof cleans a month. Across the network, we generated over \u00a368,000 in new booking revenue in a single month. (Page 4 of the prospectus)" },
        { question: "What kind of equipment is included with the franchise fee?", answer: "The franchise fee includes professional cleaning equipment such as industrial gutter vacs, RO/DI systems, carbon poles, and specialist roof cleaning tools. (Page 35 of the prospectus)"},
        { question: "Are funding options available for the initial investment?", answer: "Yes, YetiClean offers full funding options with guidance from our specialist Loans Consultant. (Page 20 of the prospectus)" },
        { question: "What training does YetiClean provide for franchisees?", answer: "Comprehensive training includes online courses, hands-on practical training in roof and exterior cleaning, and ongoing support for both franchisees and employees. (Page 22 of the prospectus)"},
        { question: "What marketing support does YetiClean provide?", answer: "YetiClean runs a proven marketing system that generates nearly 1,000 leads per month across the network, with conversion rates above 17%. We handle your ads, A/B testing, automated campaigns, and social media management. (Page 10 and Page 26 of the prospectus)" },
        { question: "What territories are protected for franchisees?", answer: "Franchisees receive exclusive rights to operate within a protected territory of wealthy homeowners. (Page 4 of the prospectus)"},
        { question: "What is the earning potential in the first year?", answer: "Projected earnings in the first year include a turnover of \u00a396,188 and a profit of \u00a358,388. Our best franchisees are already exceeding these figures. (Page 4 of the prospectus)"},
        { question: "What ongoing support does YetiClean provide?", answer: "Franchisees have access to ongoing support, business coaching, CRM tools, and marketing analytics. (Page 30 of the prospectus)"},
        { question: "What type of customers does YetiClean target?", answer: "YetiClean focuses on property owners, leveraging demographic and geographic precision to target high-value leads for roof cleaning and exterior maintenance services. (Page 10 of the prospectus)" },
        { question: "How does YetiClean help generate repeat business?", answer: "The YetiClean CRM sends automated maintenance reminders, generates referrals, and schedules recurring services - turning one-off roof cleans into long-term customers. (Page 12 of the prospectus)"},
        { question: "Can franchisees offer additional services?", answer: "Yes, the system supports upselling and cross-selling of services like gutter cleaning, patio cleaning, and exterior maintenance alongside roof cleaning. (Page 13 of the prospectus)" },
        { question: "How does the YetiClean CRM system benefit franchisees?", answer: "The YetiClean CRM handles lead capture, scheduling, customer communication, and marketing automation. (Page 24 of the prospectus)" },
        { question: "What are the key performance metrics for franchisees?", answer: "Metrics include customer satisfaction scores, job completion rates, and marketing ROI, all tracked automatically. (Page 16 of the prospectus)" },
        { question: "What type of individual is ideal for this franchise opportunity?", answer: "Ideal candidates are entrepreneurial, customer-focused, tech-savvy, and committed to long-term business success. (Page 33 of the prospectus)" }
    ];

    const AFTER = {
        imageUrl: 'https://yeticlean.co.uk/static/media/after_gutter.6c619372ab9a3fb1a145.webp'
    };
    const BEFORE = {
        imageUrl: 'https://yeticlean.co.uk/static/media/before_gutter.16d142d7394dcebde568.webp'
    };


    const videoContainerStyle  = {
        width: '100%',
        aspectRatio: '16/9',
    };


    const scrollToForm = () => {
        setStep(0);
        setFranchiseType("");
        const formElement = document.getElementById('stepper-form');
        if (formElement) {
            formElement.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
        const sequence = [
            { card: 'man_in_a_van', delay: 600 },
            { card: '', delay: 1000 },
            { card: 'management', delay: 1400 },
            { card: '', delay: 1800 },
            { card: 'man_in_a_van', delay: 2200 },
            { card: '', delay: 2600 },
            { card: 'management', delay: 3000 },
            { card: '', delay: 3400 },
        ];
        sequence.forEach(({ card, delay }) => {
            setTimeout(() => setHighlightCard(card), delay);
        });
    };


    function getClickID() {
        const match = document.cookie.match(/(^|;)\s*_fbc=([^;]+)/);
        return match ? match[2] : null;
    }

    function getBrowserID() {
        const fbp = document.cookie.match(/_fbp=([^;]+)/);
        return fbp ? fbp[1] : null;
    }

    const handleSubmit = async () => {
        if (!name || !phone || !email) {
            setMessage("Please fill in all fields.");
            return;
        }
        setLoading(true);
        setMessage("");
        let facebook_data = {}
        facebook_data['user_agent'] = navigator.userAgent;
        facebook_data['browser_id'] = getBrowserID();
        facebook_data['click_id'] = getClickID();
        try {
            console.log("=== SUBMITTING LEAD ===");
            console.log("franchiseType:", franchiseType);
            console.log("territoryLocation:", territoryLocation);
            console.log("=======================");
            await AddLead(name, phone, email, franchiseType, territoryLocation, facebook_data);
            downloadPDF()
            setMessage("Your Free Franchise Pack has been emailed to you!");
            setName("");
            setPhone("");
            setEmail("");
            setFranchiseType("");
            setTerritoryLocation("");
            setStep(0);
            navigate("/thank-you");

        } catch (error) {
            setMessage("Error submitting the form. Please try again.");
        }
        setLoading(false);
    };

    const downloadPDF = async () => {
        const pdfUrl = "https://yeti-clean-media.lon1.cdn.digitaloceanspaces.com/prospectus/YetiClean_Prospectus.pdf";

        const response = await fetch(pdfUrl);
        const blob = await response.blob();

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = "YetiClean_Prospectus.pdf";
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const isSelected = (type) => franchiseType === type;

    const isHighlighted = (type) => highlightCard === type;

    const typeCardSx = (type) => ({
        flex: 1,
        minHeight: '180px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        padding: '24px',
        cursor: 'pointer',
        border: '2px solid',
        borderColor: (isSelected(type) || isHighlighted(type)) ? 'primary.500' : 'neutral.300',
        outline: isSelected(type) ? '2px solid' : 'none',
        outlineColor: 'primary.500',
        outlineOffset: '0px',
        borderRadius: '12px',
        backgroundColor: isSelected(type) ? 'primary.500' : 'background.surface',
        color: isSelected(type) ? 'white' : 'inherit',
        transition: 'all 0.3s ease',
        transform: isHighlighted(type) ? 'scale(1.03)' : 'scale(1)',
        boxShadow: isHighlighted(type) ? '0 0 0 3px rgba(25, 118, 210, 0.3)' : 'none',
        '&:hover': {
            borderColor: 'primary.500',
            backgroundColor: isSelected(type) ? 'primary.600' : 'primary.500',
            color: 'white',
            '& .MuiTypography-root': { color: 'white' },
            '& .MuiSvgIcon-root': { color: 'white' },
        },
    });

    return (
        <>
            <Box sx={(theme) => ({width: '1200px', marginLeft: 'auto', marginRight: 'auto',
                [theme.breakpoints.down('md')]: {
                    boxSizing: 'border-box', width: '100%', pl: 2, pr: 2
                }
            })}>

                <Box sx={{width: '100%', justifyContent: 'center', textAlign: 'center', marginTop: '15px'}}>
                    <img style={{width: "200px"}} src={logo}/>
                </Box>
                <Box sx={{width: '100%', justifyContent: 'center', textAlign: 'center', marginTop: '30px'}}>
                    <Typography sx={{fontSize:  { xs: '35px', md: '50px' }}} level={"h1"}>
                        Build Your <Typography color={"primary"}>{"\u00A3"}8,000+/Month</Typography> Roof Cleaning Business
                    </Typography>
                    <Divider sx={{marginTop: '10px', marginBottom: '10px'}}><Typography sx={{ textAlign: 'center', whiteSpace: 'normal' }}>Watch our video below to find out how our franchisees are doing it</Typography></Divider>
                </Box>

                <Box sx={{display: 'flex', width: '100%', justifyContent: 'center', marginTop: '20px'}}>
                    <div style={videoContainerStyle}>
                        <iframe
                            width="100%"
                            height="100%"
                            src={`https://www.youtube.com/embed/kF1jhSBOHh8`}
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        ></iframe>
                    </div>
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
                        marginTop: '20px',
                        gap: 2,
                    }}
                >
                    <Sheet sx={{display: 'flex', flexDirection: 'column', textAlign: 'center', alignItems: 'center', padding: '10px'}}>
                        <AccountBalance color={"primary"} sx={{fontSize: { xs: '35px', md: '50px' }}}/>
                        <Typography color="primary" level={"title-sm"}>Funding Options Available</Typography>
                    </Sheet>
                    <Sheet sx={{display: 'flex', flexDirection: 'column', textAlign: 'center', alignItems: 'center', padding: '10px'}}>
                        <CurrencyPound color={"primary"} sx={{fontSize: { xs: '35px', md: '50px' }}}/>
                        <Typography color="primary" level={"title-sm"}>Franchisees earning {"\u00A3"}8k+/month</Typography>
                    </Sheet>
                    <Sheet sx={{display: 'flex', flexDirection: 'column', textAlign: 'center', alignItems: 'center', padding: '10px'}}>
                        <AdsClick color={"primary"} sx={{fontSize: { xs: '35px', md: '50px' }}}/>
                        <Typography color="primary" level={"title-sm"}>900+ leads generated monthly</Typography>
                    </Sheet>
                    <Sheet sx={{display: 'flex', flexDirection: 'column', textAlign: 'center', alignItems: 'center', padding: '10px'}}>
                        <DiamondOutlined color={"primary"} sx={{fontSize: { xs: '35px', md: '50px' }}}/>
                        <Typography color="primary" level={"title-sm"}>Backed with custom software</Typography>
                    </Sheet>
                    <Sheet sx={{display: 'flex', flexDirection: 'column', textAlign: 'center', alignItems: 'center', padding: '10px'}}>
                        <PinDrop color={"primary"} sx={{fontSize: { xs: '35px', md: '50px' }}}/>
                        <Typography color="primary" level={"title-sm"}>Your Own Protected Territory</Typography>
                    </Sheet>
                    <Sheet sx={{display: 'flex', flexDirection: 'column', textAlign: 'center', alignItems: 'center', padding: '10px'}}>
                        <FileCopy color={"primary"} sx={{fontSize: { xs: '35px', md: '50px' }}}/>
                        <Typography color="primary" level={"title-sm"}>38+ Page Prospectus</Typography>
                    </Sheet>
                </Box>

                <Box sx={{width:  { xs: '100%', md: '80%' }, marginLeft: 'auto', marginRight: 'auto', justifyContent: 'center', textAlign: 'center', marginTop: '20px'}}>
                    <Typography level={"h1"} sx={{marginBottom: '10px'}}>The Perfect Franchise to Start in 2026</Typography>
                    <Typography color="primary" level={"h3"}>Roof Cleaning is Booming - Our Franchisees Are Earning {"\u00A3"}8,000+/Month</Typography>
                    <Typography level={"body-md"}>
                        Looking for a proven business in a high-demand, low-competition market? YetiClean franchisees are cleaning 10+ roofs a month and generating serious revenue from day one. With our proven marketing system pulling in nearly 1,000 leads a month across the network, advanced tools, and expert support, there has never been a better time to get started.
                    </Typography>
                </Box>

            </Box>
            <Box sx={{
                width: '100vw',
                position: 'relative',
                left: '50%',
                right: '50%',
                marginLeft: '-50vw',
                marginRight: '-50vw',
                backgroundColor: '#f0f4f8',
                py: 6,
                mt: 4,
            }}>
                <Typography color="primary" level={"h3"} sx={{ textAlign: 'center', mb: 3, px: 2, fontSize: { xs: '20px', md: '24px' } }}>Ready to take the next step? Fill out the form and download our prospectus</Typography>
                <Sheet
                    id="stepper-form"
                    variant="outlined"
                    sx={(theme) => ({
                        width: '1200px',
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        borderRadius: '16px',
                        padding: { xs: '20px', md: '40px' },
                        boxSizing: 'border-box',
                        [theme.breakpoints.down('md')]: {
                            width: 'calc(100% - 16px)',
                            mx: 1,
                        },
                    })}
                >
                    <Stepper sx={{ width: '100%', mb: 3, '& .MuiStep-root': { fontSize: { xs: '12px', md: '14px' } } }}>
                        <Step
                            indicator={
                                <StepIndicator variant={step >= 0 ? "solid" : "outlined"} color="primary">
                                    {step > 0 ? <Check sx={{ color: 'white' }} /> : "1"}
                                </StepIndicator>
                            }
                        >
                            Franchise Type
                        </Step>
                        <Step
                            indicator={
                                <StepIndicator variant={step >= 1 ? "solid" : "outlined"} color="primary">
                                    {step > 1 ? <Check sx={{ color: 'white' }} /> : "2"}
                                </StepIndicator>
                            }
                        >
                            Your Territory
                        </Step>
                        <Step
                            indicator={
                                <StepIndicator variant={step >= 2 ? "solid" : "outlined"} color="primary">
                                    3
                                </StepIndicator>
                            }
                        >
                            Your Details
                        </Step>
                    </Stepper>

                    {step === 0 && (
                        <Box>
                            <Typography level="h3" sx={{ textAlign: 'center', mb: 1 }}>
                                What type of franchise are you interested in?
                            </Typography>
                            <Typography level="body-md" sx={{ textAlign: 'center', mb: 3, color: 'neutral.600' }}>
                                Select the option that best suits you
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
                                <Box
                                    onClick={() => { setFranchiseType('man_in_a_van'); setHighlightCard(''); }}
                                    sx={typeCardSx('man_in_a_van')}
                                >
                                    <LocalShipping sx={{ fontSize: '60px', color: isSelected('man_in_a_van') ? 'white' : 'neutral.500' }} />
                                    <Typography level="h4" sx={{ color: isSelected('man_in_a_van') ? 'white' : 'inherit' }}>Man in a Van</Typography>
                                    <Typography level="body-sm" sx={{ textAlign: 'center', color: isSelected('man_in_a_van') ? 'rgba(255,255,255,0.85)' : 'neutral.600' }}>
                                        Build your business hands-on. You're the first operator in your brand new van, with the goal of growing the business, replacing yourself on the tools, and eventually scaling to multiple vans.
                                    </Typography>
                                </Box>
                                <Box
                                    onClick={() => { setFranchiseType('management'); setHighlightCard(''); }}
                                    sx={typeCardSx('management')}
                                >
                                    <BusinessCenter sx={{ fontSize: '60px', color: isSelected('management') ? 'white' : 'neutral.500' }} />
                                    <Typography level="h4" sx={{ color: isSelected('management') ? 'white' : 'inherit' }}>Management Franchise</Typography>
                                    <Typography level="body-sm" sx={{ textAlign: 'center', color: isSelected('management') ? 'rgba(255,255,255,0.85)' : 'neutral.600' }}>
                                        You manage the team and grow the business. Hire employees, run multiple vans, and scale to a bigger operation from the start.
                                    </Typography>
                                </Box>
                            </Box>
                            <Button
                                size="lg"
                                sx={{ mt: 3, width: '100%' }}
                                disabled={!franchiseType}
                                onClick={() => setStep(1)}
                            >
                                Continue
                            </Button>
                        </Box>
                    )}

                    {step === 1 && (
                        <Box>
                            <Typography level="h3" sx={{ textAlign: 'center', mb: 1 }}>
                                Which territory are you interested in?
                            </Typography>
                            <Typography level="body-md" sx={{ textAlign: 'center', mb: 3, color: 'neutral.600' }}>
                                Enter a town, city, or county where you'd like to operate
                            </Typography>
                            <Box sx={{ maxWidth: '500px', mx: 'auto' }}>
                                <FormLabel>
                                    <PinDrop sx={{ mr: 0.5, fontSize: '18px' }} /> Preferred Location
                                </FormLabel>
                                <input
                                    ref={autocompleteInputRef}
                                    type="text"
                                    placeholder="e.g. Manchester, Surrey, Bristol..."
                                    defaultValue={territoryLocation}
                                    onChange={(e) => setTerritoryLocation(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        fontSize: '16px',
                                        border: '1px solid #cdd7e1',
                                        borderRadius: '8px',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        backgroundColor: 'white',
                                        fontFamily: 'inherit',
                                    }}
                                />
                                <Typography level="body-xs" sx={{ mt: 1, color: 'neutral.500' }}>
                                    Start typing and select from the suggestions. Each franchisee gets an exclusive, protected territory.
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                                    <Button
                                        variant="outlined"
                                        size="lg"
                                        sx={{ flex: 1 }}
                                        onClick={() => setStep(0)}
                                        startDecorator={<ArrowBack />}
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        size="lg"
                                        sx={{ flex: 2 }}
                                        disabled={!territoryLocation}
                                        onClick={() => setStep(2)}
                                    >
                                        Continue
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {step === 2 && (
                        <Box>
                            <Typography level="h3" sx={{ textAlign: 'center', mb: 1 }}>
                                Enter your details to download the prospectus
                            </Typography>
                            <Typography level="body-md" sx={{ textAlign: 'center', mb: 3, color: 'neutral.600' }}>
                                We'll send you the full franchise pack straight away
                            </Typography>
                            <Box sx={{ maxWidth: '500px', mx: 'auto' }}>
                                <FormLabel sx={{ mt: 1 }}>Full Name</FormLabel>
                                <Input size="lg" sx={{ backgroundColor: 'white' }} value={name} onChange={(e) => setName(e.target.value)} placeholder="John Smith" />

                                <FormLabel sx={{ mt: 2 }}>Phone Number</FormLabel>
                                <Input size="lg" sx={{ backgroundColor: 'white' }} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07700 900000" />

                                <FormLabel sx={{ mt: 2 }}>Email Address</FormLabel>
                                <Input size="lg" sx={{ backgroundColor: 'white' }} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="john@example.com" />

                                {message && (
                                    <Typography color="danger" level="body-sm" sx={{ mt: 1 }}>{message}</Typography>
                                )}

                                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                                    <Button
                                        variant="outlined"
                                        size="lg"
                                        sx={{ flex: 1 }}
                                        onClick={() => setStep(1)}
                                        startDecorator={<ArrowBack />}
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        size="lg"
                                        sx={{ flex: 2 }}
                                        onClick={handleSubmit}
                                        disabled={loading}
                                        startDecorator={<FileDownload />}
                                    >
                                        {loading ? "Submitting..." : "Download Prospectus"}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Sheet>
            </Box>
            <Box sx={(theme) => ({width: '1200px', marginLeft: 'auto', marginRight: 'auto',
                [theme.breakpoints.down('md')]: {
                    boxSizing: 'border-box', width: '100%', pl: 2, pr: 2
                }
            })}>
                <Box sx={{
                    marginTop: '50px',
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                    gap: 3,
                    width: '100%',
                }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography level={"title-md"} sx={{margin: '5px'}}>Watch the Webinar</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <img style={{ width: "100%", maxWidth: '330px', height: 'auto' }} src={video_thumbnail}/>
                        </Box>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography level={"title-md"} sx={{margin: '5px'}}>Unlock Your Opportunity Pack</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <img style={{ maxHeight: '190px', height: 'auto' }} src={franchise_pack}/>
                        </Box>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography level={"title-md"} sx={{margin: '5px'}}>Schedule a Call with Mike</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <img style={{ maxHeight: '190px', height: 'auto' }} src={calendar}/>
                        </Box>
                    </Box>
                </Box>

                <Sheet variant="soft" sx={{display: 'flex', justifyContent: 'center', width: '100%', marginTop: { xs: '50px', md: '100px' }, padding: { xs: '20px', md: '50px' }, boxSizing: 'border-box',  flexDirection: { xs: 'column', md: 'row' }}}>
                    <Box sx={{width: { xs: '100%', md: '700px' }}}>
                        <Chip sx={{color: 'white', textWrap: 'wrap', padding: '10px'}} color={"primary"}>Next Step: Unlock Your Franchise Opportunity Pack</Chip>

                        <Box sx={{display: 'flex', marginTop: '15px', width:  { xs: '100%', md: '80%' }, }}>
                            <Box sx={{ display: { xs: 'none', md: 'flex' }}}>
                                <img style={{
                                    height: "120px",
                                    marginLeft: '5px',
                                    marginRight: '5px'
                                }} src={franchise_pack}/>
                            </Box>
                            <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                                <Typography level={"h3"}>Download Your Franchise Opportunity Pack</Typography>
                                <Typography color={"primary"} level={"h4"}>Find out if this is the franchise for you...</Typography>
                            </Box>
                        </Box>
                        <Divider
                            sx={{
                                marginTop: '15px',
                                blockSize: '2px',
                                width: { xs: '100%', md: '80%' }
                            }}
                        />
                        <Box sx={{width:  { xs: '100%', md: '80%' }}}>
                            <Typography fontSize={"18px"}  sx={{marginTop: '10px'}}>
                                Making the right decision starts with having the right information. The <Typography fontWeight={750}>YetiClean Franchise Opportunity Pack</Typography> gives you everything you need to evaluate whether this franchise is the perfect fit for your goals.
                            </Typography>
                            <Typography fontSize={"18px"} sx={{marginTop: '10px'}}>
                                Packed with essential details like projected earnings, territory demographics, and a case study on how our proven system generates results, this pack will help you make an <Typography fontWeight={750}>informed, confident decision</Typography> about starting your YetiClean journey.
                            </Typography>
                            <Typography fontSize={"18px"} sx={{marginTop: '10px'}}>
                                Don't miss out on the opportunity to explore the business model, potential income, and tools that set YetiClean apart. Sign up today and take the first step towards building your future!
                            </Typography>
                            <Button onClick={scrollToForm} size={"lg"} sx={{marginTop: '10px', width: '100%'}} startDecorator={<LockSharp/>}>Download the prospectus</Button>
                        </Box>


                    </Box>
                    <Box sx={{width: { xs: '100%', md: '400px' }, height: '100%', margin: 'auto', marginTop: { xs: '10px', md: 'inherit' } }}>
                        <Card color="primary" variant="solid" >
                            <Typography sx={{color: 'white'}} level={"title-lg"}>What's Inside?</Typography>
                            <Divider
                                sx={{
                                    backgroundColor: 'white',
                                }}
                            />
                            <List sx={{ color: 'white' }}>
                                <ListItem>
                                    <ListItemDecorator sx={{ color: 'white' }}>
                                        <FileCopy sx={{ color: 'white' }} />
                                    </ListItemDecorator>
                                    <Typography sx={{ color: 'white' }}>Your 30+ Page Franchise Prospectus</Typography>
                                </ListItem>
                                <ListItem>
                                    <ListItemDecorator sx={{ color: 'white' }}>
                                        <TrendingUp sx={{ color: 'white' }} />
                                    </ListItemDecorator>
                                    <Typography sx={{ color: 'white' }}>Your Projected Earnings Report</Typography>
                                </ListItem>
                                <ListItem>
                                    <ListItemDecorator sx={{ color: 'white' }}>
                                        <MapRounded sx={{ color: 'white' }} />
                                    </ListItemDecorator>
                                    <Typography sx={{ color: 'white' }}>Sample Territory Map & Demographics</Typography>
                                </ListItem>
                                <ListItem>
                                    <ListItemDecorator sx={{ color: 'white' }}>
                                        <CalendarMonthOutlined sx={{ color: 'white' }} />
                                    </ListItemDecorator>
                                    <Typography sx={{ color: 'white' }}>Ability to Schedule a Call Access</Typography>
                                </ListItem>
                                <ListItem>
                                    <ListItemDecorator sx={{ color: 'white' }}>
                                        <CallMadeRounded sx={{ color: 'white' }} />
                                    </ListItemDecorator>
                                    <Typography sx={{ color: 'white' }}>Scheduled Call Sample Questions</Typography>
                                </ListItem>
                                <ListItem>
                                    <ListItemDecorator sx={{ color: 'white' }}>
                                        <AdsClick sx={{ color: 'white' }} />
                                    </ListItemDecorator>
                                    <Typography sx={{ color: 'white' }}>YetiClean Paid Ads Case Study</Typography>
                                </ListItem>
                            </List>
                        </Card>
                    </Box>
                </Sheet>
                <Box sx={{display: 'flex', width: '100%', marginTop: { xs: '50px', md: '100px' },  flexDirection: { xs: 'column', md: 'row' }}}>
                    <Box sx={{marginRight:  { xs: '0px', md: '15px' }}}>
                        <Typography level={"h1"} sx={{ fontSize: { xs: '24px', md: '32px' } }}>YetiClean Franchise Details</Typography>
                        <Typography  sx={{marginTop: '5px', marginBottom: '5px'}} level={"h2"} fontWeight={500}>Franchise Fee: <Typography fontWeight={750}>{"\u00A3"}12,950</Typography></Typography>
                        <Typography level={"h4"} fontWeight={500}>Projected Earnings</Typography>
                        <Typography fontSize={"18px"}>Joining YetiClean isn't just about starting a business; it's about building a highly profitable and scalable future in the booming roof cleaning industry. Here's what you can expect:</Typography>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                            gap: '15px',
                            marginTop: '15px',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            padding: '10px',
                        }}>
                            <Box sx={{
                                padding: '8px 0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                flexDirection: {xs: 'column', md: 'row'}
                            }}>
                                <Typography level="body-lg">Year 1 Turnover:</Typography>
                                <Typography level="title-lg">{"\u00A3"}96,188</Typography>
                            </Box>
                            <Box sx={{
                                padding: '8px 0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                flexDirection: {xs: 'column', md: 'row'}
                            }}>
                                <Typography level="body-lg">Year 2 Turnover:</Typography>
                                <Typography level="title-lg">{"\u00A3"}179,683</Typography>
                            </Box>
                            <Box sx={{
                                padding: '8px 0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                flexDirection: {xs: 'column', md: 'row'}
                            }}>
                                <Typography level="body-lg">Year 3 Turnover:</Typography>
                                <Typography level="title-lg">{"\u00A3"}215,709</Typography>
                            </Box>
                            <Box sx={{
                                padding: '8px 0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                flexDirection: {xs: 'column', md: 'row'}

                            }}>
                                <Typography level="body-lg">Year 1 Profit:</Typography>
                                <Typography level="title-lg">{"\u00A3"}58,388</Typography>
                            </Box>
                            <Box sx={{
                                padding: '8px 0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                flexDirection: {xs: 'column', md: 'row'}
                            }}>
                                <Typography level="body-lg">Year 2 Profit:</Typography>
                                <Typography level="title-lg">{"\u00A3"}101,069</Typography>
                            </Box>
                            <Box sx={{
                                padding: '8px 0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                flexDirection: {xs: 'column', md: 'row'}
                            }}>
                                <Typography level="body-lg">Year 3 Profit:</Typography>
                                <Typography level="title-lg">{"\u00A3"}132,621</Typography>
                            </Box>
                        </Box>


                        <Typography sx={{marginTop: '15px'}} level={"body-sm"}>* This projection is based on a single van and a single employee. We expect franchisees to scale to multiple vans and employees as soon as possible, significantly increasing these figures.</Typography>
                        <Typography sx={{marginTop: '5px'}} level={"body-sm"}>* This projection is based on current performance but does not form any binding contract or promise of income</Typography>
                    </Box>
                    <Box sx={{width: { xs: '100%', md: '500px' }}}>
                        <Card color="primary"
                              size="lg"
                              variant="plain"
                              sx={{padding: { xs: '0px', md: '15px' }, marginTop: { xs: '15px', md: '0px' }}}
                        >
                            <Typography level={"h2"}>Funding Options</Typography>
                            <Typography sx={{marginTop: '5px'}}>There are many funding options available for the YetiClean franchise opportunity.</Typography>
                            <Typography sx={{marginTop: '5px'}}>The majority of franchisees go through some kind of funding to acquire their franchise.</Typography>
                            <Typography sx={{marginTop: '5px'}}>Please get in touch with us to learn more...</Typography>
                            <Button onClick={scrollToForm} size={"lg"} sx={{marginTop: '10px'}} startDecorator={<LockSharp/>}>Download the prospectus</Button>
                        </Card>
                    </Box>
                </Box>

                <Divider sx={{marginTop: '15px', marginBottom: '15px', padding: '2px'}}/>

                <Box sx={(theme) => ({display: 'flex', marginTop: { xs: '50px', md: '100px' },
                    [theme.breakpoints.down('md')]: {
                        flexDirection: 'column'
                    }})}>
                    <Box sx={{marginRight: { xs: 0, md: '15px' }}}>
                        <Typography level={"h1"} sx={{ fontSize: { xs: '24px', md: '32px' } }}>High Demand,</Typography>
                        <Typography level={"h1"} sx={{ fontSize: { xs: '24px', md: '32px' } }}>Low Competition,</Typography>
                        <Typography level={"h1"} sx={{ fontSize: { xs: '24px', md: '32px' } }}>Huge Earnings.</Typography>
                        <Typography level={"h3"} color={"primary"}>How Our Franchisees Are Earning {"\u00A3"}8,000+ a Month,</Typography>
                        <Typography level={"h3"} color={"primary"}>And building a business for life.</Typography>

                        <Typography  sx={{ fontSize: '18px',  color: 'text.primary', marginTop: '15px'}}>
                            "At YetiClean, we've built a franchise model based on <span style={{ fontWeight: 'bold', color: '#112A74' }}>proven results</span> and real market demand. Our marketing system generates nearly 1,000 leads per month across the network, with conversion rates above 17%. In January alone, our franchisees created over {"\u00A3"}68,000 in new booking revenue. Our top operators are completing 10+ roof cleans a month and pulling in <span style={{ fontWeight: 'bold', color: '#112A74' }}>{"\u00A3"}8,000+ in monthly revenue</span>, with job values ranging from {"\u00A3"}80 to {"\u00A3"}500 and huge upsell potential.
                        </Typography>

                        <Typography  sx={{ fontSize: '18px',color: 'text.primary', mt: '10px' }}>
                            What makes YetiClean truly unique is our focus on <span style={{ fontWeight: 'bold', color: '#112A74' }}>recurring revenue</span>. Through our <span style={{ fontWeight: 'bold', color: '#112A74' }}>YetiClean CRM</span>, every customer becomes a long-term asset with automated reminders, upsells, and seasonal offers. This isn't just a one-time business model - it's a system designed to provide predictable, steady income in an industry with high demand and low competition.
                        </Typography>

                        <Typography sx={{ fontSize: '18px', color: 'text.primary', mt: '10px' }}>
                            If you're looking for a proven franchise opportunity with real revenue and real growth potential, YetiClean is the perfect choice. We've removed the guesswork, eliminated the risks, and created a pathway for you to succeed in a booming market."
                        </Typography>
                    </Box>
                    <Sheet  sx={(theme) => ({display: 'flex', marginTop: 'auto', marginBottom: 'auto',
                        [theme.breakpoints.down('md')]: {
                            marginLeft: 'auto', marginRight: 'auto',
                            marginTop: '15px', marginBottom: '15px',
                    }})}>
                        <Box sx={{width: { xs: '100%', md: '270px' }, maxWidth: '270px', height: '100%', marginLeft: { xs: 0, md: '20px' }}}>
                            <ReactBeforeSliderComponent
                                firstImage={AFTER}
                                secondImage={BEFORE}
                            />
                        </Box>
                    </Sheet>

                </Box>

                <Box sx={{marginTop: { xs: '50px', md: '100px' }}}>
                    <Typography level="h1" sx={{textAlign: 'center', fontSize: { xs: '24px', md: '32px' }}}>Custom Technology & Systems</Typography>
                    <Typography level="h3" sx={{textAlign: 'center'}} color={"primary"}>Built For Your Ongoing Success</Typography>

                    <Typography sx={{marginBottom: '10px', marginTop: '15px', fontSize: '18px'}}>At YetiClean, we've built our franchise model on <Typography fontWeight={500}>five proven pillars</Typography> that set you up for success from day one. These pillars work together seamlessly to create a business that's scalable, profitable, and sustainable.</Typography>
                    <Typography sx={{marginBottom: '10px', fontSize: '18px'}}>From marketing to technology, we handle the complexities for you. Our <Typography fontWeight={500}>done-for-you marketing system</Typography> generates high-quality roof cleaning leads and automates customer engagement, whilst our advanced YetiClean CRM ensures every aspect of your operations is streamlined and efficient.</Typography>
                    <Typography sx={{marginBottom: '10px', fontSize: '18px'}}>To help you grow confidently, we provide tailored <Typography fontWeight={500}>one-on-one coaching</Typography> and continuous development tools that empower you to scale your business effectively. With data-driven tracking, you'll always have clear insights into your performance, enabling you to optimise and achieve consistent revenue growth.</Typography>
                    <Typography sx={{marginBottom: '15px', fontSize: '18px'}}>These <Typography fontWeight={500}>five pillars</Typography> aren't just a framework - they're a tested and proven system designed to help every franchisee succeed in an industry with high demand and low competition. With YetiClean, you're not just joining a franchise; you're building a business with a foundation for long-term success.</Typography>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
                        gap: 2,
                        width: '100%',
                        justifyItems: 'center',
                    }}>
                        {[
                            { src: crm, label: 'Advanced CRM' },
                            { src: marketing, label: 'Specialised Marketing' },
                            { src: profits, label: 'Compounding Profits' },
                            { src: scaling, label: 'Business Scaling' },
                            { src: tracking, label: 'Tracking & Optimisation' },
                        ].map((item, i) => (
                            <Box key={i} sx={{ maxWidth: '150px', width: '100%' }}>
                                <img style={{ width: "100%", border: '2px solid #112A74' }} src={item.src}/>
                                <Typography sx={{textAlign: 'center'}} color={"primary"}>{item.label}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Button onClick={scrollToForm} size={"lg"} sx={{marginTop: '5px',  width: { xs: '100%', md: '500px' }}} startDecorator={<LockSharp/>}>Find Out More About the 5 Pillars In Our Prospectus</Button>

                <Box sx={(theme) => ({marginTop: { xs: '50px', md: '100px' }, display: 'flex', justifyContent: 'space-between', [theme.breakpoints.down('md')]: {
                        flexDirection: 'column'
                    }
                    })}>
                    <Box sx={{marginRight: { xs: 0, md: '15px' }}}>
                        <Typography level="h1" sx={{ fontSize: { xs: '24px', md: '32px' } }}>Proven Marketing That</Typography>
                        <Typography level="h1" sx={{ fontSize: { xs: '24px', md: '32px' } }}>Delivers Real Results</Typography>
                        <Typography level="h3" color={"primary"}>Nearly 1,000 leads a month across the network...</Typography>

                        <Typography sx={{marginBottom: '10px', marginTop: '5px'}}>Our high-performance Facebook and Google Ads campaigns generate nearly <Typography fontWeight={500}>1,000 leads per month</Typography> across the YetiClean network, with conversion rates consistently above 17%. With expertly designed campaigns and our proprietary YetiClean CRM, we've streamlined the customer journey - delivering leads directly to our franchisees and ensuring seamless booking, quoting, and communication.</Typography>
                        <Typography sx={{marginBottom: '10px'}}>Join a system built on real results, where your business growth is backed by data-driven marketing strategies and cutting-edge technology.</Typography>

                    </Box>


                    <Box sx={(theme) => ({display: 'flex', mt: 'auto', mb: 'auto', width: '500px', [theme.breakpoints.down('md')]: {
                            mt: '15px', mb: '15px', mr: 'auto', ml: 'auto', width: '100%'
                        }
                    })}>
                        <img style={{
                            width: "100%",
                            height: "100%",
                        }} src={ads}/>
                    </Box>
                </Box>
                <Button onClick={scrollToForm} size={"lg"} sx={{marginTop: '5px',  width: { xs: '100%', md: '500px' }}} startDecorator={<LockSharp/>}>Read more on the marketing model in the prospectus</Button>

                <Box sx={(theme) => ({marginTop: { xs: '50px', md: '100px' }, display: 'flex', justifyContent: 'space-between', [theme.breakpoints.down('md')]: {
                        flexDirection: 'column'
                    }
                })}>
                    <Box sx={{marginRight: { xs: 0, md: '15px' }}}>
                        <Typography level="h1" sx={{ fontSize: { xs: '24px', md: '32px' } }}>Recurring, Compounding Revenue</Typography>
                        <Typography level="h3" color={"primary"}>How we focus on the compounding</Typography>
                        <Typography sx={{marginBottom: '10px', marginTop: '5px'}}>YetiClean's model is built on creating long-term, predictable income through recurring and compounding revenue streams. With our YetiClean CRM, customers are automatically engaged through reminders, seasonal offers, and upsells, turning one-off roof cleans into repeat business.</Typography>
                        <Typography sx={{marginBottom: '10px'}}>This compounding effect grows over time, as satisfied customers generate referrals, 5-star reviews, and additional service bookings. Your business transitions from relying on new leads to leveraging a self-sustaining system of repeat clients and referrals, ensuring stability and increasing profitability year after year.</Typography>
                        <Typography sx={{marginBottom: '10px'}}>At YetiClean, every customer becomes a valuable, lasting part of your business's success.</Typography>

                    </Box>

                    <Box sx={(theme) => ({display: 'flex', mt: 'auto', mb: 'auto', width: '500px', flexDirection: 'column',[theme.breakpoints.down('md')]: {
                            mt: '15px', mb: '15px', mr: 'auto', ml: 'auto', flexDirection: 'column', width: '100%', alignItems: 'center'
                        }
                    })}>
                        <img style={{
                            width: "100%",
                            height: "100%",
                            maxWidth: '400px'
                        }} src={territory}/>
                        <Typography sx={{textAlign: 'center'}} >As a YetiClean Franchisee, you get access to a territory map of at least <Typography fontWeight={500}>120,000 ABC1&2s</Typography> - these are wealthy homeowners, our ideal customers. Find out more...</Typography>
                    </Box>
                </Box>

                <Button onClick={scrollToForm} size={"lg"} sx={{marginTop: '5px',  width: { xs: '100%', md: '500px' }}} startDecorator={<LockSharp/>}>Read more on the compounding model in the prospectus</Button>


                <Box sx={{marginTop: { xs: '50px', md: '100px' }, display: 'flex', justifyContent: 'space-between' }}>
                    <Box sx={{marginRight: { xs: 0, md: '15px' }}}>
                        <Typography level="h1" sx={{ fontSize: { xs: '24px', md: '32px' } }}>Who we're looking for</Typography>
                        <Typography level="h3" color={"primary"}>The Ideal YetiClean Franchisee</Typography>

                        <Typography sx={{marginBottom: '10px', marginTop: '5px'}}>YetiClean is perfect for ambitious, driven individuals who are ready to build a successful roof cleaning business. The ideal franchisee is customer-focused, thrives on delivering exceptional service, and embraces proven systems to achieve growth. A strong work ethic, entrepreneurial mindset, and the ability to lead a team are key traits for success.</Typography>
                        <Typography sx={{marginBottom: '10px'}}>Tech-savvy and adaptable, the ideal franchisee uses YetiClean's advanced tools, like the YetiClean CRM, to streamline operations and maximise efficiency. With a focus on long-term business growth and excellent communication skills, they're committed to building strong customer relationships and a thriving local reputation.</Typography>
                        <Button onClick={scrollToForm} size={"lg"} sx={{marginTop: '5px',  width: { xs: '100%', md: '500px' }}} startDecorator={<LockSharp/>}>Find out if you match in the prospectus</Button>
                    </Box>
                    <Box sx={{mt: 'auto', mb: 'auto',  display: { xs: 'none', md: 'flex' }}}>
                        <img style={{
                            width: "300px",
                            height: "100%",
                        }} src={franchisee}/>
                    </Box>
                </Box>
                <Sheet color={"primary"} variant={"solid"} sx={{marginTop: { xs: '50px', md: '150px' }, display: 'flex', justifyContent: 'space-between', padding: { xs: '20px', md: '50px' }, flexDirection: { xs: 'column', md: 'row' }}}>
                    <Box>
                        <Typography sx={{color: 'white', fontSize: { xs: '24px', md: '32px' }}} level={"h1"}>Your Franchise Prospectus</Typography>
                        <Typography sx={{color: 'white'}} level={"h3"}>What's Inside?</Typography>
                        <Box sx={{display: 'flex', mt: '10px', flexDirection: { xs: 'column', md: 'row' }}}>
                            <List>
                                {["Funding Options", "YetiClean's 5 Pillar System", "Your Path to \u00A38,000+/Month", "Proven Marketing", "Expert Team"].map((option, index) => (
                                    <ListItem key={index} sx={{ gap: "5px" }}>
                                        <ListItemDecorator>
                                            <Check sx={{ color: "white", fontSize: "30px" }} />
                                        </ListItemDecorator>
                                        <Typography level="title-lg" sx={{ color: "white" }}>
                                            {option}
                                        </Typography>
                                    </ListItem>
                                ))}
                            </List>
                            <List>
                                {["Ongoing Training & Coaching", "Business Development", "Candidate Criteria", "Franchise Package"].map((option, index) => (
                                    <ListItem key={index} sx={{ gap: "5px" }}>
                                        <ListItemDecorator>
                                            <Check sx={{ color: "white", fontSize: "30px" }} />
                                        </ListItemDecorator>
                                        <Typography level="title-lg" sx={{ color: "white" }}>
                                            {option}
                                        </Typography>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                        <Typography sx={{color: 'white'}} level={"body-lg"}>On top of everything in the prospectus you'll also gain access to the entire franchise opportunity pack including projected earnings report, next steps document and much more...</Typography>
                        <Button
                            onClick={scrollToForm}
                            variant="solid"
                            size="lg"
                            sx={{
                                marginTop: '25px',
                                color: 'black',
                                background: 'white',
                                '&:hover': {
                                    background: 'white',
                                    color: 'black',
                                },
                            }}
                            startDecorator={<FileDownload />}
                        >
                            Download Franchise Prospectus
                        </Button>
                    </Box>
                    <Box sx={{display: { xs: 'none', md: 'flex' }, justifyContent: 'center', alignItems: 'center'}}>
                        <img style={{
                            width: "100%",
                            height: "auto",
                        }} src={franchise_pack}/>
                    </Box>
                </Sheet>

                <Box sx={{marginTop: { xs: '50px', md: '100px' }, width: '100%', ml: 'auto', mr: 'auto',}}>
                    <Typography color={"primary"} sx={{textAlign: 'center', mb: 1, fontSize: { xs: '24px', md: '32px' }}} level={"h1"}>Frequently Asked Questions</Typography>
                    <Typography sx={{textAlign: 'center', mb: 1}} level={"h3"}>Click to view answer</Typography>
                    <AccordionGroup size="lg">
                        {faqData.map((item, index) => (
                            <Accordion key={index}>
                                <AccordionSummary >{item.question}</AccordionSummary>
                                <AccordionDetails>{item.answer}</AccordionDetails>
                            </Accordion>
                        ))}
                    </AccordionGroup>
                </Box>
                <Button onClick={scrollToForm} color="primary" size="lg" sx={{display: 'flex', mr: 'auto', ml: 'auto', marginTop: '25px'}} startDecorator={<FileDownload/>}>Download Franchise Prospectus</Button>
                <Sheet
                    sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginTop: { xs: '50px', md: '150px' }, width: '100%', minHeight: '250px', px: 2, boxSizing: 'border-box'}}>
                    <img style={{width: "250px"}} src={logo}/>
                    <Typography level={"title-sm"} sx={{mt: 1}}>Get in touch: mike@mikesmithfranchising.co.uk</Typography>
                    <Typography level={"title-xs"} sx={{mt: 5}} color={"primary"}>Copyright @ YetiClean Ltd 2026</Typography>

                </Sheet>


            </Box>
        </>
    )

}
